import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, styled } from '@mui/material';
import { Grid } from '@mui/system';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useFormat } from 'common/hooks';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { IValuationState } from 'dashboard409a/states/features/valuation';
import { RootStateType } from 'dashboard409a/states/store';
import { formatMD } from 'dashboard409a/utilities';
import { EnterpriseValue } from '../EnterpriseValue';
import { VOSDProps } from './types';
import { EnterpriseValueProps } from '../EnterpriseValue/types';

const PREFIX = 'ValuationOpinionSummaryData';

const classes = {
  root: `${PREFIX}-root`,
  boxValOpinion: `${PREFIX}-boxValOpinion`,
};

const StyledGrid = styled(Grid)(() => ({
  [`& .${classes.root}`]: {
    boxShadow: 'none',
  },

  // Tight under the banner — the note belongs to it, not to the charts below.
  [`& .${classes.boxValOpinion}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 8,
  },
}));

const ValuationOpinionSummaryData: FC<VOSDProps> = ({ shareSpace }) => {
  const { loading, currentValuationScalarOpinion } = useSelector<RootStateType, IValuationState>(
    state => state.valuation
  );
  const { currentMeasurementDate } = useSelector((state: RootStateType) => state.global);
  const currentDateValue = useMemo(() => currentMeasurementDate?.date, [currentMeasurementDate]);
  const [format] = useFormat({ units: REGULAR_UNIT });
  const lgColumns = shareSpace ? 6 : 12;

  const updatedAsDate = useMemo(() => {
    if (currentMeasurementDate) {
      return formatMD({ date: currentDateValue });
    }
    return '';
  }, [currentDateValue, currentMeasurementDate]);

  // A 409A opinion is good for 12 months from the measurement date.
  const expiry = useMemo(() => {
    if (!currentDateValue) {
      return null;
    }
    const expiresAt = moment(currentDateValue).add(12, 'months');
    if (expiresAt.isBefore(moment())) {
      return { text: 'Valuation expired', expired: true };
    }
    const months = expiresAt.diff(moment(), 'months');
    return {
      text:
        months >= 1
          ? `Valuation expires in ${months} month${months === 1 ? '' : 's'}`
          : 'Valuation expires in less than a month',
      expired: false,
    };
  }, [currentDateValue]);

  const [updatedValuationScalarOpinion, setUpdatedValuationScalarOpinion] = useState({
    weighted_market_enterprise_value: '',
    marketable_value_per_common_share: '',
    non_marketable_value_per_common_share: '',
  });

  const enterpriseValueProps: EnterpriseValueProps = {
    enterpriseData: updatedValuationScalarOpinion,
    isLoading: loading,
    format,
    expiry,
  };

  useEffect(() => {
    if (currentValuationScalarOpinion) {
      setUpdatedValuationScalarOpinion({
        weighted_market_enterprise_value: currentValuationScalarOpinion.weighted_market_enterprise_value,
        marketable_value_per_common_share: currentValuationScalarOpinion.marketable_value_per_common_share,
        non_marketable_value_per_common_share: currentValuationScalarOpinion.non_marketable_value_per_common_share,
      });
    }
  }, [currentValuationScalarOpinion]);

  return (
    <StyledGrid size={{ xs: 12, lg: lgColumns }}>
      <Card className={classes.root}>
        <CardContent>
          <EnterpriseValue {...enterpriseValueProps} />
          <Box className={classes.boxValOpinion} display="flex" justifyContent="space-between" alignItems="center">
            <small style={{ color: '#2E6BD8', fontSize: '0.75rem', fontStyle: 'italic' }}>
              Scalar opinion updated as of {updatedAsDate}.
            </small>
            {expiry && (
              <small style={{ color: expiry.expired ? 'red' : 'green', fontSize: '0.75rem', fontStyle: 'italic' }}>
                {expiry.text}
              </small>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* TODO(dashboard-409-revamp): the Valuation Methods editor
          (components/ValuationMethodsDetail) is unmounted — the redesign
          doesn't show it, but it was the only place analysts could edit the
          opinion from the dashboard. Pending a product decision on whether
          that editing lives here; the wiring (link + showValMethods state +
          displayTable) is in this file's git history. */}
    </StyledGrid>
  );
};

export default ValuationOpinionSummaryData;
